import {
	CheckCircle,
	Download,
	DownloadSimple,
	Hospital,
} from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import PrimaryButton from '../../../components/atoms/Button';
import toast from 'react-hot-toast';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ROUTES from '../../../shared/constants/routes';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../redux/store';
import { capitalizeText, useStorage } from '../../../shared/utils/general.util';
import useAddToCart from '../../../hooks/useAddToCart';
import { CategoryName } from '../../../@types/Products.type';
import Modal from '../../../components/molecules/Modal/Modal';
import { acceptCompliance } from '../../../services/profile.service';
import { PRODUCTS_SLUG } from '../../../shared/constants/SlugsProducts';
import LOCAL_STORAGE_KEY from '../../../shared/constants/localStorageKey';
import { setShowModalCheckout } from '../../../redux/slices/plans.slice';
import Loading from '../../../components/atoms/Loading';
import { useService } from '../../../hooks/useService';
import { postImss } from '../../../services/imss.service';
import {
	ANALYTICS_EVENTS,
	EVENT_TYPES,
} from '../../../shared/constants/events';
import { sendEvent } from '../../../services/user-interaction.service';
import { useMediaQuery } from '@mui/material';

const Header = () => {
	return (
		<div className='flex flex-col md:justify-center md:items-center md:text-center'>
			<Hospital
				weight='duotone'
				size={24}
				className='text-h-primary mb-2'
			/>
			<p className='font-medium text-lg'>
				Prepara tu información de seguridad social
			</p>
			<p className='text-neutral-500 font-light'>
				Las plataformas te la pedirán para acceder a los beneficios de esta
				nueva ley y poder seguir trabajando con ellos sin contratiempos luego
				del 22 de junio.
			</p>
		</div>
	);
};

const Step2 = () => {
	const dispatch = useDispatch();
	const { getNssInfo, getAcquisitions } = useService();
	const { handleAddCustomProduct, isLoading: isLoadingAddToCart } =
		useAddToCart();
	const navigate = useNavigate();
	const taxpayer = useSelector((state: RootState) => state.taxProfile.taxPayer);
	const userProfile = useSelector((state: RootState) => state.profile.user);
	const { nss } = useSelector((state: RootState) => state.profile);
	const { tokenSession } = useSelector((state: RootState) => state.auth);
	const products = useSelector(
		(state: RootState) => state.productList.products,
	);
	const [searchParams, setSearchParams] = useSearchParams();
	const imssProduct = products
		?.find((item) => item.name === CategoryName.OtherPlans)
		?.products?.find((item) => item?.slug === 'info_imss');

	const [curp] = useState<string>(taxpayer?.official_id || '');
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [showModal, setShowModal] = useState<boolean>(false);
	const [selectedOption, setSelectedOption] = useState<string>('');

	const { acquisitions } = useSelector((state: RootState) => state.productList);

	const isMobile = useMediaQuery('(max-width: 768px)');

	const haveNssInfoAcquired = acquisitions.some(
		(acquisition) => acquisition?.product?.slug === PRODUCTS_SLUG.NSS_INFO,
	);

	const fullName = taxpayer?.name
		? taxpayer?.name
		: `${userProfile?.first_name} ${userProfile?.father_surname}`;

	const handleAddToCart = async () => {
		if (!imssProduct?.default_product_version_id) return;
		let payload: {
			product_version_id?: number;
			additional_information:
				| {
						regimes: string[];
						month?: number;
						year: number;
						product_package?: string;
				  }
				| Record<string, string>;
		}[] = [
			{
				product_version_id: imssProduct?.default_product_version_id,
				additional_information: {
					postal_code: '12345',
				},
			},
		];

		const newParams = new URLSearchParams(searchParams);
		newParams.set('redirect_to_success_page', 'false');
		setSearchParams(newParams);

		await handleAddCustomProduct({
			payload,
			navigateToCart: true,
			resetOrders: true,
		});
		dispatch(setShowModalCheckout(true));
	};

	const handleSubmit = async (e?: React.FormEvent<HTMLFormElement> | any) => {
		try {
			e?.preventDefault();
			setIsLoading(true);
			await new Promise((resolve) => setTimeout(resolve, 5000));
			await acceptCompliance(
				'terms_and_conditions_nss_validation',
				tokenSession,
			);
			useStorage().setItem(LOCAL_STORAGE_KEY.ACCEPTED_NSS_VALIDATION, 'true');
			await getNssInfo();
			if (nss?.response_body?.status === 'processing') return;
			const response = await postImss({
				is_register_candidate: false,
				partner_address: {
					postal_code: '0',
				},
			});
			if (response?.status === 'failed') {
				toast.error(
					'Ocurrió un error al consultar tu NSS, si el problema persiste, por favor contacta a soporte.',
				);
				setShowModal(false);
				return;
			}
			await getNssInfo();
			try {
				await sendEvent({
					user_id: userProfile?.id,
					event: ANALYTICS_EVENTS.NSS_VALIDATION,
					source: isMobile ? 'app' : 'web',
					event_payload: {
						event_type: EVENT_TYPES.NSS_VALIDATION_STARTED,
						status: 'started',
						sended_by: 'user',
					},
				});
			} catch (e) {}
		} catch (error) {
			toast.error('Error al validar tu CURP');
		} finally {
			setIsLoading(false);
		}
	};

	useEffect(() => {
		let interval: NodeJS.Timeout;

		if (nss?.response_body?.status === 'processing') {
			interval = setInterval(() => {
				getNssInfo();
			}, 30000);
		}

		return () => {
			if (interval) {
				clearInterval(interval);
			}
		};
	}, [nss?.response_body?.status]);

	useEffect(() => {
		getAcquisitions();

		sendEvent({
			user_id: userProfile?.id,
			event: ANALYTICS_EVENTS.IMSS_SECTION_TAB_NSS,
			source: isMobile ? 'app' : 'web',
			event_payload: {},
		});
	}, []);

	const isNssAccepted =
		useStorage().getItem(LOCAL_STORAGE_KEY.ACCEPTED_NSS_VALIDATION) === 'true';
	const nssIsProcessing = nss?.response_body?.status === 'processing';
	const isNssValidated = nss?.response_body?.social_security_number;

	if (nssIsProcessing && isNssAccepted) {
		return (
			<div className='bg-white rounded-xl flex flex-col gap-3 items-center relative py-8 text-center px-6'>
				<Loading />
				<div className='flex flex-col items-center'>
					<p className='font-medium text-lg'>
						Estamos consultando tu número de seguridad social
					</p>
					<p className='text-neutral-500 font-light'>
						Este proceso puede tardar hasta 24 horas, al finalizar te enviaremos
						un correo para que puedas volver a está sección a consultar tu
						información.
					</p>

					<div className='mt-4 text-neutral-500 font-light bg-neutral-100 rounded-lg p-2'>
						En caso de que está pantalla no se actualice en el transcurso de 24
						horas, por favor contacta a soporte{' '}
						<span
							className='underline cursor-pointer'
							onClick={() => navigate(ROUTES.SUPPORT)}
						>
							desde aquí.
						</span>
					</div>
				</div>
			</div>
		);
	}

	if (
		haveNssInfoAcquired &&
		nss?.response_body?.social_security_number
	) {
		return (
			<div className='bg-white rounded-xl p-4 flex flex-col relative'>
				<Header />
				<div className='bg-neutral-100/70 p-4 rounded-lg mt-4'>
					<p className='font-medium text-base'>{capitalizeText(fullName)}</p>
					<div className='mt-2 flex flex-col gap-2'>
						{[
							`CURP: ${curp}`,
							`NSS: ${nss?.response_body?.social_security_number}`,
						]?.map((item, index) => {
							return (
								<div
									key={index}
									className='flex items-center gap-2'
								>
									<CheckCircle 
										weight='fill'
										size={20}
										className='text-green-600'
									/>
									<p className='font-light'>{item}</p>
								</div>
							);
						})}
					</div>
				</div>
				<div className='w-full mt-5 flex flex-col gap-2'>
					<iframe
						src={`https://docs.google.com/viewer?url=${encodeURIComponent(nss?.response_body?.ssn_proof_doc_url!)}&embedded=true`}
						className='w-full h-[500px] rounded-lg'
					/>
					{nss?.response_body?.ssn_proof_doc_url && (
						<PrimaryButton
							label='Descargar PDF oficial del NSS'
							variant='tertiary'
							icon={<Download size={16} />}
							sx='w-full'
							onClick={() => {
								window.open(nss?.response_body?.ssn_proof_doc_url!, '_blank');
							}}
						/>
					)}

					{nss?.response_body?.ssn_card_doc_url && (
						<PrimaryButton
							label='Descargar tarjeta del NSS'
							variant='tertiary'
							icon={<Download size={16} />}
							sx='w-full'
							onClick={() => {
								window.open(nss?.response_body?.ssn_card_doc_url!, '_blank');
							}}
						/>
					)}
				</div>
			</div>
		);
	}

	if (isNssValidated && isNssAccepted) {
		return (
			<div className='bg-white rounded-xl p-4 flex flex-col gap-3 relative'>
				<Header />

				<div className='bg-neutral-100/70 p-4 rounded-lg mt-4'>
					<p className='font-medium text-base'>{capitalizeText(fullName)}</p>
					<div className='mt-2 flex flex-col gap-2'>
						{[
							`CURP: ${curp}`,
							`NSS: ${nss?.response_body?.social_security_number}`,
						]?.map((item, index) => {
							return (
								<div
									key={index}
									className='flex items-center gap-2'
								>
									<CheckCircle
										weight='fill'
										size={20}
										className='text-green-600'
									/>
									<p className='font-light'>{item}</p>
								</div>
							);
						})}
					</div>
				</div>

				<p className='w-full text-center my-2'>
					Te recomendamos obtener tus datos completos del IMSS por $25 mxn y
					tenerlos a la mano
				</p>

				<PrimaryButton
					loading={isLoadingAddToCart.products && selectedOption === 'nss_card'}
					label='Tarjeta de seguridad social'
					variant='secondary'
					beforeIcon={
						<DownloadSimple
							size={16}
							weight='bold'
						/>
					}
					sx='w-full px-4'
					onClick={() => {
						handleAddToCart();
						setSelectedOption('nss_card');
					}}
				/>
				<PrimaryButton
					loading={isLoadingAddToCart.products && selectedOption === 'nss_full'}
					label='Datos completos del IMSS'
					variant='secondary'
					beforeIcon={
						<DownloadSimple
							size={16}
							weight='bold'
						/>
					}
					sx='w-full px-4'
					onClick={() => {
						handleAddToCart();
						setSelectedOption('nss_full');
					}}
				/>
			</div>
		);
	}

	return (
		<div className='bg-white rounded-xl p-4 flex flex-col gap-6 relative'>
			<Modal
				open={showModal}
				onClose={() => setShowModal(false)}
				showCloseButton={false}
				sx='!p-0 rounded-lg overflow-hidden w-[95%] max-w-[700px] max-h-[94dvh] overflow-y-auto'
			>
				<div className='p-4'>
					<p className='font-bold'>Consulta de número de seguridad social</p>
					<p className='font-light text-sm'>Términos y condiciones de uso</p>

					<hr className='my-4' />

					<iframe
						src='https://heru-static-assets.s3.us-east-2.amazonaws.com/nss_validation_terms_and_conditions.pdf'
						className='w-full h-[60dvh] rounded-lg mx-auto mb-3'
					/>

					<div className='flex flex-col gap-2'>
						<PrimaryButton
							label='Acepto los términos y condiciones'
							onClick={handleSubmit}
							loading={isLoading}
						/>
						<PrimaryButton
							variant='tertiary'
							label='Cancelar'
							onClick={() => setShowModal(false)}
							loading={isLoadingAddToCart.products}
						/>
					</div>
				</div>
			</Modal>

			<Header />

			{curp ? (
				<form
					className='w-full'
					onSubmit={handleSubmit}
				>
					<p className='font-medium text-xs'>Revisa tu CURP</p>
					<button
						className='font-light text-xs mt-1 mb-3 text-neutral-600'
						onClick={() => navigate(ROUTES.SUPPORT)}
					>
						Si no es correcto, puedes contactar a soporte{' '}
						<span className='underline'>desde aquí.</span>
					</button>
					<div className='rounded-lg p-2 border text-neutral-600 border-neutral-300 w-full outline-none text-base'>
						{curp?.toUpperCase()}
					</div>

					<PrimaryButton
						type='button'
						label='Obtener mi número de seguridad social'
						sx='mt-4'
						onClick={() => {
							setShowModal(true);
						}}
					/>
				</form>
			) : (
				<div className='flex flex-col gap-2 items-center justify-center border border-dashed border-neutral-300 rounded-lg p-4'>
					<p className='font-medium text-xs'>Estamos consultando tu CURP</p>
					<Loading />
					<button
						className='font-light text-xs mt-1 mb-3 text-neutral-600'
						onClick={() => navigate(ROUTES.SUPPORT)}
					>
						Si tu CURP no se actualiza en las próximas 24 horas, por favor
						contacta a soporte <span className='underline'>desde aquí.</span>
					</button>
				</div>
			)}
		</div>
	);
};

export default Step2;
